import { Controller, useFormContext } from 'react-hook-form';
import Select from 'react-select';
import { inputStyles } from '~/components/Vendors/styles';
import { DollarInput } from '~/common/form/Inputs';
import UploadDocument from './UploadDocuments';
import { SelectOption } from '~/types/globals';
import { useFundRequestContext } from '~/context/FundRequestProvider';

interface MedicalExpenseFormProps {
    categories: SelectOption[];
    onDocumentClick?: (slideIndex: number) => void;
    selectedDocumentIndex?: number;
}

const MedicalExpenseForm = ({ categories, onDocumentClick, selectedDocumentIndex }: MedicalExpenseFormProps) => {
    const { formKey, expenseItemId, glCodes } = useFundRequestContext();
    const { setValue, register, watch, control, getValues, formState: { errors } } = useFormContext();
    const filteredCategories = categories.filter(
        (c) => ['food_incidentals', 'travel_mileage'].indexOf(c.value) === -1
    );

    const currentItem = formKey ? watch(formKey) : null;
    const descriptionValue = watch(`${formKey}.description`) || '';
    const descriptionLength = descriptionValue.length;
    const maxDescriptionLength = 60;

    return (
        <div className='flex gap-4 divide-x divide-bodydark'>
            <div className='w-full'>
                <div className='mt-3 w-[100%]'>
                    <label
                        htmlFor='mid-primary-medical-reason'
                        className='block font-bold'
                    >
                        Select Category
                    </label>
                    <select
                        disabled
                        value={currentItem?.category}
                        id='mid-primary-medical-reason'
                        className={`${inputStyles} bg-grey w-full`}
                    >
                        <option value='' disabled>
                            Category
                        </option>
                        {filteredCategories.map((c, i) => {
                            return (
                                <option value={c.id} key={i}>
                                    {c.label}
                                </option>
                            );
                        })}
                    </select>
                </div>
                <div className='mb-3 flex gap-4 pt-[10px]'>
                    <div className='w-50'>
                        <label
                            htmlFor={`${formKey}.expense_date`}
                            className='font-semibold'
                        >
                            Expense Date <span className='text-red'>*</span>
                        </label>
                        <input
                            type='date'
                            className={`${inputStyles} w-full`}
                            {...register(`${formKey}.expense_date`)}
                        />
                    </div>
                    <div className='w-50'>
                        <label
                            htmlFor={`${formKey}.end_date`}
                            className='font-semibold'
                        >
                            End Date (optional)
                        </label>
                        <input
                            type='date'
                            className={`${inputStyles} w-full`}
                            {...register(`${formKey}.end_date`, {
                                setValueAs: (value) => value === '' ? null : value
                            })}
                        />
                    </div>
                </div>
                <div className='mb-3 flex gap-4'>
                    <div className='w-50'>
                        <DollarInput
                            name={`${formKey}.amount`}
                            label='Total'
                            control={control}
                            required={true}
                            defaultValue={getValues(`${formKey}.amount`) || ''}
                            placeholder='$0.00'
                            setValue={setValue}
                        />
                    </div>
                </div>
                <div className='mb-2 flex-1'>
                    <div className='flex justify-between items-center mb-1'>
                        <label htmlFor='total' className='font-semibold'>
                            Description <span className='text-red'>*</span>
                        </label>
                        <span className='text-sm text-bodydark'>
                            {descriptionLength}/{maxDescriptionLength}
                        </span>
                    </div>
                    <input
                        className={`p-[10px] text-[16px]/[20px] border-[2px] rounded-[4px] focus:border-[3px] focus:outline-none ${descriptionValue ? 'border-black' : 'border-gray-300'} w-full`}
                        maxLength={maxDescriptionLength}
                        {...register(`${formKey}.description`, {
                            required: true,
                            maxLength: {
                                value: maxDescriptionLength,
                                message: `Description must be ${maxDescriptionLength} characters or less`
                            },
                            pattern: {
                                value: /^[^"]*$/,
                                message: 'Description cannot contain double quote characters (")'
                            }
                        })}
                    />
                    {errors[formKey]?.description?.message && (
                        <span className="text-red-500 text-sm mt-1 block">
                            {String(errors[formKey]?.description?.message)}
                        </span>
                    )}
                </div>
                <UploadDocument
                    expenseItem={currentItem}
                    setSlideIndex={onDocumentClick}
                    selectedIndex={selectedDocumentIndex}
                />
                <div className='mt-5'>
                    <label htmlFor='total' className='block font-semibold'>
                        GL Code
                    </label>
                    <Controller
                        name={`${formKey}.gl_code`}
                        control={control}
                        render={({ field }) => (
                            <Select
                                {...field}
                                value={glCodes.filter(
                                    (option: SelectOption) =>
                                        option.value ===
                                        getValues(`${formKey}.gl_code`)
                                )}
                                onChange={(option) => {
                                    field.onChange(option?.value);
                                }}
                                options={glCodes}
                                formatOptionLabel={(option: SelectOption) => 
                                    `${option.value} - ${option.label}`
                                }
                            />
                        )}
                    />

                    {/* <select
                        defaultValue={`${formKey}.gl_code`}
                        className={`${inputStyles} w-50`}
                        {...register(`${formKey}.gl_code`)}
                    >
                        <option value='' disabled>
                            Select
                        </option>
                        {glCodes.map((c, i) => {
                            return (
                                <option value={c.value} key={i}>
                                    {c.label}
                                </option>
                            );
                        })}
                    </select> */}
                </div>
            </div>
        </div>
    );
};

export default MedicalExpenseForm;
